// OMD-29: 뷰어(EmailView) 리사이즈 시 media query 재반영을 위한 순수 로직.
// getSafeElement 은 @media 브랜치를 bake 시점 폭으로 선택해 인라인하므로, 렌더 이후 폭이 바뀌어도
// @media 기반 레이아웃 전환이 일어나지 않는다. 폭이 원본 @media breakpoint 구간을 "교차"할 때만
// getSafeElement 을 재계산한다(B안). 여기의 함수들은 DOM 비의존 순수 로직이라 단위 테스트로 고정한다.

// 뷰어 폭이 실제로 도달 가능한 대역. 이 범위 밖 breakpoint(예: min-width:60px 이미지 트릭,
// min-width:2000px 등)는 뷰어 폭이 교차할 수 없어 재계산을 유발하지 않으므로 목록에서 제외한다.
export const VIEWER_WIDTH_BAND = { min: 320, max: 1200 } as const

// 크기 가드 임계 (design.md §구현 타당성). 요소 수는 첫 bake 전 측정(사전 필터), 렌더 시간은
// stylesheet 복잡도까지 반영하는 보조 기준. 둘 중 하나라도 초과하면 리사이즈 재계산을 비활성한다.
export const SIZE_GUARD = { maxElements: 2000, maxRenderMs: 150 } as const

/**
 * CSS `@media` condition 문자열 목록에서 width breakpoint(px) 경계를 추출한다.
 * - `min-width`/`max-width` 만 대상. `device-width`/`orientation`/`print` 등은 제외.
 * - `em`/`rem` 은 remSize(px)로 환산. 그 외 단위(px)는 그대로.
 * - VIEWER_WIDTH_BAND 밖 값은 제외(도달 불가·이미지 트릭).
 * - 오름차순 정렬 + 중복 제거된 경계 목록을 반환한다.
 *
 * @param conditionTexts CSSMediaRule.conditionText 문자열 배열(getSafeElement 이 CSSOM 에서 수집)
 * @param remSize        root font-size(px). em/rem 환산에 사용(기본 16).
 */
export function parseWidthBreakpoints(conditionTexts: string[], remSize = 16): number[] {
  const breakpoints = new Set<number>()
  const widthPattern = /(?:min|max)-width\s*:\s*(\d+(?:\.\d+)?)\s*(px|em|rem)/gi

  for (const condition of conditionTexts) {
    // device-width 계열은 iframe 폭 조정으로 재현 불가 → 범위 밖.
    if (/device-width/i.test(condition)) continue

    for (const match of condition.matchAll(widthPattern)) {
      const value = parseFloat(match[1])
      const unit = match[2].toLowerCase()
      const px = unit === 'px' ? value : value * remSize
      const rounded = Math.round(px)
      if (rounded >= VIEWER_WIDTH_BAND.min && rounded <= VIEWER_WIDTH_BAND.max) {
        breakpoints.add(rounded)
      }
    }
  }

  return Array.from(breakpoints).sort((a, b) => a - b)
}

/**
 * 폭이 속한 구간 인덱스를 반환한다. 경계는 `max-width:N`(= width ≤ N 이 모바일) 의미에 맞춰
 * `width ≤ bp` 를 작은 구간에 귀속시킨다. breakpoints 는 오름차순 가정.
 *
 * 예) breakpoints=[600]: width 600 → 0(모바일), 601 → 1(데스크톱).
 */
export function getZoneIndex(width: number, breakpoints: number[]): number {
  let index = 0
  while (index < breakpoints.length && width > breakpoints[index]) index++
  return index
}

/** 두 폭이 서로 다른 breakpoint 구간에 속하면(=재계산 필요) true. */
export function crossesZone(prevWidth: number, newWidth: number, breakpoints: number[]): boolean {
  if (breakpoints.length === 0) return false
  return getZoneIndex(prevWidth, breakpoints) !== getZoneIndex(newWidth, breakpoints)
}

/**
 * 크기 가드: 리사이즈 재계산을 허용할지 판정한다.
 * 요소 수가 임계 이상이거나, 첫 렌더 시간이 임계 초과면 비활성(false).
 *
 * @param elementCount 본문 요소 수(doc.querySelectorAll('*').length)
 * @param lastRenderMs 첫 getSafeElement 소요(ms). 미측정이면 null.
 */
export function isRecalcAllowed(elementCount: number, lastRenderMs: number | null): boolean {
  if (elementCount >= SIZE_GUARD.maxElements) return false
  if (lastRenderMs != null && lastRenderMs > SIZE_GUARD.maxRenderMs) return false
  return true
}
